import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Paper,
    Alert,
    CircularProgress
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { api } from '../../services/api';
import { ErrorDisplay } from '../../components/common/ErrorDisplay';

const validationSchema = Yup.object({
    email: Yup.string()
        .email('Invalid email address')
        .required('Email is required')
});

export const ForgotPassword: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const formik = useFormik({
        initialValues: {
            email: ''
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            setError(null);
            try {
                await api.post('/api/v1/auth/forgot-password', values);
                setSuccess(true);
            } catch (err: any) {
                setError(err.response?.data?.detail || 'An error occurred');
            } finally {
                setLoading(false);
            }
        }
    });

    if (success) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    p: 2
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        maxWidth: 400,
                        width: '100%',
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h5" component="h1" gutterBottom>
                        Check Your Email
                    </Typography>
                    <Typography variant="body1" color="text.secondary" paragraph>
                        If an account exists with this email address, you will receive a password reset link.
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/login')}
                        sx={{ mt: 2 }}
                    >
                        Return to Login
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                p: 2
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 4,
                    maxWidth: 400,
                    width: '100%'
                }}
            >
                <Typography variant="h5" component="h1" gutterBottom align="center">
                    Forgot Password
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph align="center">
                    Enter your email address and we'll send you a link to reset your password.
                </Typography>

                {error && (
                    <ErrorDisplay
                        error={error}
                        userMessage={typeof error === 'string' ? error : 'Failed to send password reset email.'}
                    />
                )}

                <form onSubmit={formik.handleSubmit}>
                    <TextField
                        fullWidth
                        id="email"
                        name="email"
                        label="Email Address"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        error={formik.touched.email && Boolean(formik.errors.email)}
                        helperText={formik.touched.email && formik.errors.email}
                        margin="normal"
                        disabled={loading}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading}
                        sx={{ mt: 3, mb: 2 }}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Send Reset Link'}
                    </Button>

                    <Button
                        fullWidth
                        variant="text"
                        onClick={() => navigate('/login')}
                        disabled={loading}
                    >
                        Back to Login
                    </Button>
                </form>
            </Paper>
        </Box>
    );
}; 